import { z } from "zod";
import type { Operation } from "../types";

/**
 * Generic typed-import endpoints (the "import a CSV as X" pattern). One contract
 * serves every capability: the `target` names the server importer (crm-leads,
 * hiring-candidates, finance-transactions, …); `mapping` is column → target
 * field. Parsing (CSV / TSV / JSON) is derived from the filename server-side.
 */

const previewResponse = z.object({
  columns: z.array(z.string()),
  sample_rows: z.array(z.record(z.string(), z.string())),
  total_rows: z.number(),
});

const preview: Operation<
  void,
  void,
  { filename: string; file_base64: string },
  z.infer<typeof previewResponse>
> = {
  method: "POST",
  path: () => "/api/data-import/preview",
  body: z.object({
    filename: z.string().min(1),
    file_base64: z.string().min(1),
  }),
  response: previewResponse,
};

const runResponse = z.object({
  target: z.string(),
  total_rows: z.number(),
  imported_rows: z.number(),
  failed_rows: z.number(),
  errors: z.array(z.object({ row: z.number(), reason: z.string() })),
});

const run: Operation<
  void,
  void,
  {
    target: string;
    filename: string;
    file_base64: string;
    mapping: Record<string, string>;
  },
  z.infer<typeof runResponse>
> = {
  method: "POST",
  path: () => "/api/data-import/run",
  body: z.object({
    target: z.string().min(1),
    filename: z.string().min(1),
    file_base64: z.string().min(1),
    mapping: z.record(z.string(), z.string()),
  }),
  response: runResponse,
};

export const dataImport = { preview, run };
